import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Clock, Building2, FileText, Check, X, Eye, Loader2, AlertTriangle, Shield, LogOut, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Component for document buttons - extracted to avoid nested maps
function DocumentButtons({ docs, onView }) {
  if (!docs || docs.length === 0) return null;
  
  const items = docs.map(function(d) {
    return (
      <Button 
        key={d.id} 
        variant="outline" 
        onClick={function() { onView(d); }}
        className="border-purple-500/30 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20"
      >
        <FileText className="h-4 w-4 mr-2" />
        {d.filename}
        <Eye className="h-4 w-4 ml-2" />
      </Button>
    );
  });
  
  return (
    <div className="mt-4">
      <p className="text-slate-500 text-xs mb-2">Documents fournis</p>
      <div className="flex flex-wrap gap-2">{items}</div>
    </div>
  );
}

// Component for a single user card
function UserCard({ user, onApprove, onReject, onViewDoc, processing }) {
  const initial = user.name ? user.name.charAt(0).toUpperCase() : 'U';
  const dateStr = user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : '';
  
  function handleViewDoc(doc) {
    onViewDoc(user, doc);
  }
  
  return (
    <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-slate-700/50">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white text-xl font-bold">
                {initial}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{user.name}</h3>
                <p className="text-slate-400">{user.email}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-slate-500 text-xs mb-1">Entreprise</p>
                <p className="text-white font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-400" />
                  {user.nom_entreprise}
                </p>
              </div>
              {user.siret && (
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <p className="text-slate-500 text-xs mb-1">SIRET</p>
                  <p className="text-white font-mono">{user.siret}</p>
                </div>
              )}
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-slate-500 text-xs mb-1">Date inscription</p>
                <p className="text-white flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  {dateStr}
                </p>
              </div>
            </div>

            <DocumentButtons docs={user.documents} onView={handleViewDoc} />
          </div>

          <div className="flex flex-col gap-2">
            <Button 
              onClick={function() { onApprove(user); }} 
              disabled={processing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white min-w-[140px]"
              data-testid="approve-user-btn"
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <><Check className="h-4 w-4 mr-2" />Approuver</>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={function() { onReject(user); }} 
              disabled={processing}
              className="border-red-500/50 text-red-400 hover:bg-red-500/20 min-w-[140px]"
              data-testid="reject-user-btn"
            >
              <X className="h-4 w-4 mr-2" />Refuser
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AdminPendingPage() {
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documentModal, setDocumentModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  const adminToken = localStorage.getItem('adminToken');
  const adminUsername = localStorage.getItem('adminUsername');

  useEffect(function() {
    if (!adminToken) {
      navigate('/admin/login');
    }
  }, [adminToken, navigate]);

  const fetchPendingUsers = useCallback(async function() {
    if (!adminToken) return;
    setLoading(true);
    try {
      const response = await axios.get(API_URL + '/api/admin/pending-users', {
        headers: { Authorization: 'Bearer ' + adminToken }
      });
      setPendingUsers(response.data.pending_users || []);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
      toast.error('Erreur chargement des inscriptions');
    } finally {
      setLoading(false);
    }
  }, [adminToken, navigate]);

  useEffect(function() {
    fetchPendingUsers();
  }, [fetchPendingUsers]);

  async function viewDocument(user, doc) {
    try {
      const response = await axios.get(
        API_URL + '/api/admin/user/' + user.id + '/document/' + doc.id,
        { headers: { Authorization: 'Bearer ' + adminToken } }
      );
      setDocumentModal({ user: user, doc: response.data });
    } catch (error) {
      toast.error('Erreur chargement du document');
    }
  }

  async function approveUser(user) {
    setProcessing(true);
    try {
      const response = await axios.post(
        API_URL + '/api/admin/approve-user',
        { user_id: user.id },
        { headers: { Authorization: 'Bearer ' + adminToken } }
      );
      toast.success(
        <div>
          <p className="font-bold">Utilisateur approuvé !</p>
          <p className="text-sm font-mono">{response.data.login_code}</p>
          {response.data.email_sent && <p className="text-xs text-emerald-600">Email envoyé</p>}
        </div>
      );
      fetchPendingUsers();
      setDocumentModal(null);
    } catch (error) {
      const msg = error.response && error.response.data ? error.response.data.detail : 'Erreur';
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  }

  async function rejectUser() {
    if (!rejectModal) return;
    setProcessing(true);
    try {
      await axios.post(
        API_URL + '/api/admin/reject-user',
        { user_id: rejectModal.id, reason: rejectReason },
        { headers: { Authorization: 'Bearer ' + adminToken } }
      );
      toast.success('Inscription refusée');
      setRejectModal(null);
      setRejectReason('');
      fetchPendingUsers();
    } catch (error) {
      const msg = error.response && error.response.data ? error.response.data.detail : 'Erreur';
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  }

  function handleLogout() {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    navigate('/admin/login');
  }

  // Render user cards
  const userCards = pendingUsers.map(function(user) {
    return (
      <UserCard 
        key={user.id}
        user={user}
        onApprove={approveUser}
        onReject={setRejectModal}
        onViewDoc={viewDocument}
        processing={processing}
      />
    );
  });

  // Document preview content
  let docPreview = null;
  if (documentModal && documentModal.doc) {
    const doc = documentModal.doc;
    if (doc.content_type && doc.content_type.startsWith('image/')) {
      docPreview = (
        <img 
          src={'data:' + doc.content_type + ';base64,' + doc.data}
          alt={doc.filename} 
          className="max-w-full h-auto rounded-lg mx-auto" 
        />
      );
    } else if (doc.content_type === 'application/pdf') {
      docPreview = (
        <iframe 
          src={'data:application/pdf;base64,' + doc.data}
          className="w-full h-[600px] rounded-lg" 
          title={doc.filename} 
        />
      );
    } else {
      docPreview = (
        <div className="text-center py-20 text-slate-500">
          <FileText className="h-20 w-20 mx-auto mb-4" />
          <p>Aperçu non disponible pour ce type de fichier</p>
        </div>
      );
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="admin-pending-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-xl text-white">Inscriptions en attente</h1>
                <p className="text-xs text-slate-400">{adminUsername}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={function() { navigate('/admin'); }}
                className="border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700"
              >
                Retour Dashboard
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchPendingUsers}
                className="border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700"
              >
                <RefreshCw className={'h-4 w-4 mr-2 ' + (loading ? 'animate-spin' : '')} />
                Actualiser
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-purple-500" />
          </div>
        ) : pendingUsers.length === 0 ? (
          <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-slate-700/50">
            <CardContent className="text-center py-20">
              <Check className="h-16 w-16 mx-auto mb-4 text-emerald-500" />
              <h2 className="text-xl font-bold text-white mb-2">Aucune inscription en attente</h2>
              <p className="text-slate-400">Toutes les inscriptions ont été traitées.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">
                {pendingUsers.length} inscription(s) à traiter
              </h2>
            </div>
            {userCards}
          </div>
        )}
      </main>

      {/* Document Modal */}
      <Dialog open={!!documentModal} onOpenChange={function() { setDocumentModal(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {documentModal && documentModal.doc ? documentModal.doc.filename : ''}
            </DialogTitle>
            <DialogDescription>
              Document de {documentModal && documentModal.user ? documentModal.user.name : ''} - {documentModal && documentModal.user ? documentModal.user.nom_entreprise : ''}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 bg-slate-100 rounded-lg p-2 min-h-[400px]">
            {docPreview}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={function() { setDocumentModal(null); }}>Fermer</Button>
            <Button 
              onClick={function() { if (documentModal && documentModal.user) approveUser(documentModal.user); }}
              disabled={processing} 
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Approuver cet utilisateur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={!!rejectModal} onOpenChange={function() { setRejectModal(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="h-5 w-5" />
              Refuser l'inscription
            </DialogTitle>
            <DialogDescription>
              Vous êtes sur le point de refuser l'inscription de <strong>{rejectModal ? rejectModal.name : ''}</strong>.
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            <label className="text-sm font-medium mb-2 block">Raison du refus (optionnel)</label>
            <Textarea 
              value={rejectReason} 
              onChange={function(e) { setRejectReason(e.target.value); }}
              placeholder="Documents illisibles, informations incomplètes..." 
              rows={3} 
            />
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={function() { setRejectModal(null); }}>Annuler</Button>
            <Button onClick={rejectUser} disabled={processing} className="bg-red-600 hover:bg-red-700">
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <X className="h-4 w-4 mr-2" />}
              Confirmer le refus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminPendingPage;
